import type {
  AppliedLimit,
  PeriodBounds,
  SpendTotals,
} from '@/lib/api/generated/model';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DISCLOSURE_CHEVRON,
  DISCLOSURE_TRIGGER,
} from '@/shared/components/disclosure';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';

import { DISPLAY_FIGURE } from '@/features/workspace-overview/stateStyles';
import { limitHasOwnPeriod, periodPhrase } from '../limitWindow';
import { periodForecast, shortDay } from '../meterFooter';
import { exactMoney, formatMoney, formatPercent } from '../money';
import {
  THRESHOLD_CHIP,
  THRESHOLD_CHIP_BOX,
  THRESHOLD_FILL,
  THRESHOLD_HATCH,
  THRESHOLD_LABEL,
  thresholdFor,
  worseThreshold,
} from '../thresholdStyles';
import { USAGE_CARD, USAGE_CARD_GAP } from '../usageLayout';
import { BAR_HEIGHT, BAR_TRACK } from '@/shared/components/barScale';

export interface SpendMeterProps {
  spend: SpendTotals;
  limit: AppliedLimit | null;
  /** The period the page is reading, which the limit's own period may not be. */
  period: PeriodBounds;
  /**
   * The card's last line (spec 231): the forecast and the limit's renewal, which
   * belong to the figure above them rather than loose between the cards.
   */
  footer?: ReactNode;
  /** The period's runs, for the caveat on the total line (spec 253). */
  runCount?: number;
  /** For tests: the moment the forecast is taken at. */
  now?: Date;
  /**
   * Where the runs themselves are (spec 256): a link to the Tasks page, said on the
   * total line beside the not-measured count. It was a footnote under By day.
   */
  runsLink?: ReactNode;
}

/**
 * What this period cost, against what it may cost (spec 072 FR-006 – FR-010).
 *
 * The page's one primary figure, because the question a reader opens this page with is
 * "am I going to run out", and that is a ratio rather than a trend. The daily figure
 * beneath answers the follow-up, "why".
 *
 * Built from divs, like `CompositionBar` on the overview. No charting library is
 * introduced (FR-028): every one of them ships default palettes, sequential ramps and
 * gradients, and FD-12 forbids extending this palette while OD-10 records that there is
 * no ramp to extend it with — so a library would be fought on every colour it chose.
 *
 * Both values are present as text beside the bar (FR-007). A drawn length alone is not a
 * figure anyone can quote, and it is unreadable to a screen reader.
 */
export function SpendMeter({
  spend,
  limit,
  period,
  footer,
  runCount = 0,
  now,
  runsLink,
}: SpendMeterProps) {
  const caveat = (
    <Caveat
      runCount={runCount}
      unmeasuredRuns={spend.unmeasured_run_count}
      unpricedRuns={spend.unpriced_run_count}
      link={runsLink}
    />
  );
  if (limit === null)
    return <NoLimit spend={spend} footer={footer} caveat={caveat} />;

  const ratio = limit.consumed_ratio;
  const currency = limit.currency ?? 'USD';
  // A weekly limit read in October is measured against this week's spend, not the
  // month's (spec 216). The figure, the bar and the state must all be the limit's, and
  // the month's spend is said separately, so the meter never sets one against the other.
  const ownPeriod = limitHasOwnPeriod(limit, period);
  const measured = ownPeriod ? limit.consumed_amount : spend.amount;
  // Where the average day lands by the period's end (spec 253), drawn on the bar.
  const forecast = periodForecast({ spend, limit, period, now });

  return (
    <section
      className={cn(USAGE_CARD, 'flex flex-col', USAGE_CARD_GAP)}
      aria-labelledby="usage-spend-heading"
    >
      {/* On a phone "of $60.00 this month" follows the figure it qualifies
          rather than standing apart at the right edge (spec 277). */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 max-md:justify-start max-md:gap-x-2">
        <h2 id="usage-spend-heading" className="sr-only">
          Spending against this workspace&rsquo;s limit
        </h2>
        {/*
          `DISPLAY_FIGURE` carries both rules: the size, and the absence of
          `tabular-nums` — equal-width digits line up columns, there is no column here,
          and at display size they read loose and mechanical.

          It was `text-3xl`, the only non-heading `text-3xl` in the product, which made
          the one figure that most needs to look like the rest of the application the
          one that did not (spec 074 FR-003).
        */}
        <Total amount={measured} currency={currency} />
        <p className="text-muted-foreground text-sm">
          of {formatMoney(limit.amount, currency)}
          {ownPeriod ? ` ${periodPhrase(limit.period)}` : null}
          {/*
            Which limit this is, said only when it is the exception (spec 232 FR-002).
            An override tells the reader who to ask; the organization default is what
            every workspace has, so naming it on every meter was a word nobody read.
          */}
          {limit.is_override ? (
            <span className="text-muted-foreground/70">
              {' · set for this workspace'}
            </span>
          ) : null}
        </p>
        {caveat}
      </div>

      <Bar
        ratio={ratio}
        threshold={limit.threshold}
        forecastRatio={forecast?.ratio ?? null}
        forecastLine={
          forecast
            ? `At this pace, ${forecast.hedge} ${formatPercent(forecast.ratio ?? 0)} by ${shortDay(forecast.endsAt)}.`
            : null
        }
        limitLabel={`${formatMoney(limit.amount, currency)} limit`}
      />

      {/*
        The threshold as a word, not only as a colour. These three states are an
        ordered scale, which is precisely what cannot be ranked by hue (FR-030).
      */}
      <span
        className={`${THRESHOLD_CHIP_BOX} ${THRESHOLD_CHIP[limit.threshold]} self-start`}
      >
        {THRESHOLD_LABEL[limit.threshold]} · {formatPercent(ratio)}
      </span>
      {ownPeriod && (
        <p className="text-muted-foreground text-sm">
          Spent {periodPhrase(period)}: {formatMoney(spend.amount, currency)}
        </p>
      )}
      {footer}
    </section>
  );
}

/**
 * The total, with its exact amount on it (spec 232 FR-001).
 *
 * The one full-precision figure on the page (spec 072 FR-016) used to be a line of its
 * own beside the chip, the same amount said twice on one card. It now lives on the
 * total: a tooltip on hover or focus, and in the trigger's text for a screen reader
 * or a touch reader, so the exact figure stays reachable without a pointer.
 */
function Total({ amount, currency }: { amount: string; currency: string }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <p
            // Focusable so the tooltip opens from the keyboard as well as on hover.
            tabIndex={0}
            className={cn(
              DISPLAY_FIGURE,
              'focus-visible:ring-ring rounded-sm focus-visible:ring-3 focus-visible:outline-none',
            )}
          />
        }
      >
        <span>{formatMoney(amount, currency)}</span>
        <span className="sr-only">{` Exact: ${exactMoney(amount, currency)}`}</span>
      </TooltipTrigger>
      <TooltipContent>
        <span className="tabular-nums">
          Exact: {exactMoney(amount, currency)}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The bar itself, with three marks (spec 253): what is spent as the fill, where the
 * average day lands by the period's end as a hatched stretch beyond it, and the limit
 * as the track's end, with a tick and its amount.
 *
 * An overrun stays legible as an overrun rather than clamping to full (FR-009): the fill
 * is capped at the track's width, and a distinct marker sits past the end so a workspace
 * at 340% does not look identical to one at exactly 100%. A forecast past the limit
 * hatches to the end, and the sentence under the meter says so.
 *
 * The fill's colour is the worse of the actual state and the forecast's, at the
 * service's own cut-off; the chip keeps naming the actual.
 */
function Bar({
  ratio,
  threshold,
  forecastRatio,
  forecastLine,
  limitLabel,
}: {
  ratio: number;
  threshold: AppliedLimit['threshold'];
  forecastRatio: number | null;
  forecastLine: string | null;
  limitLabel: string;
}) {
  const filled = Math.min(ratio, 1);
  const over = ratio > 1;
  const ahead =
    forecastRatio !== null && forecastRatio > ratio
      ? Math.min(forecastRatio, 1)
      : null;
  const tone =
    forecastRatio === null
      ? threshold
      : worseThreshold(threshold, thresholdFor(forecastRatio));

  /*
    The fill arrives rather than appearing (spec 088 FR-011). This is the one figure the
    page exists to show, and it used to be fully formed on first paint — which reads as a
    static image rather than as a measurement.

    A *transition toward* the value, never a keyframe from zero. With motion suppressed a
    keyframe whose resting state is its start would leave a nearly-exhausted limit reading
    zero (research R6); a transition simply lands on the value. And because the width is
    state rather than a one-shot animation, a period change **moves** the fill from where
    it was to where it now is instead of dropping to zero and refilling.

    Under reduced motion the starting value *is* the final one, so there is no first frame
    at zero to suppress — the stylesheet's override would have handled the duration, but
    not that frame.
  */
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced ? filled : 0);
  const [shownAhead, setShownAhead] = useState(reduced ? (ahead ?? 0) : 0);
  useEffect(() => {
    // On the next frame, not synchronously. A synchronous set asks React to render
    // twice inside one commit, and the browser may coalesce the two so the start value
    // is never painted — leaving nothing for the transition to animate from. Waiting a
    // frame guarantees the start value is on screen before the width changes.
    const frame = requestAnimationFrame(() => {
      setShown(filled);
      setShownAhead(ahead ?? 0);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [filled, ahead]);

  const label = [
    `${formatPercent(ratio)} of this workspace's spend limit used.`,
    forecastLine,
  ]
    .filter(Boolean)
    .join(' ');

  const motion =
    'transition-[width] duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)]';

  return (
    <div className="flex flex-col gap-1">
      <div role="img" aria-label={label} className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <div
            className={`${BAR_TRACK} relative ${BAR_HEIGHT} overflow-hidden rounded-full`}
          >
            {ahead !== null ? (
              <span
                data-forecast=""
                className={`absolute inset-y-0 left-0 rounded-full ${motion} ${THRESHOLD_HATCH[tone]}`}
                style={{ width: `${String(shownAhead * 100)}%` }}
              />
            ) : null}
            <span
              data-spent=""
              className={`absolute inset-y-0 left-0 rounded-full ${motion} ${THRESHOLD_FILL[tone]}`}
              style={{ width: `${String(shown * 100)}%` }}
            />
          </div>
          {/* The limit: the track's end, marked. */}
          <span
            aria-hidden="true"
            data-limit-tick=""
            className="bg-foreground/60 absolute -inset-y-1 right-0 w-0.5 rounded-full"
          />
        </div>
        {over ? (
          /*
            A separate mark past the track, not a longer bar. The track *is* the limit —
            drawing beyond it would make the limit's own position ambiguous, and the point
            being made is precisely that this workspace went past it.
          */
          <span
            aria-hidden="true"
            className={`${BAR_HEIGHT} w-1.5 rounded-sm ${THRESHOLD_FILL[tone]}`}
          />
        ) : null}
      </div>
      <span
        aria-hidden="true"
        className={cn(
          'text-muted-foreground self-end text-xs tabular-nums',
          over && 'me-3',
        )}
      >
        {limitLabel}
      </span>
    </div>
  );
}

/**
 * The caveat, on the total line (spec 253): "11 of 56 runs not measured", with the
 * reasons behind Details. It was a full-width notice card of its own plus a pill on
 * every row, louder than the figures it qualified.
 *
 * The two counts stay named apart (spec 072 FR-019): unmeasured runs have no token
 * counts, unpriced runs have tokens and no price, and the fixes differ.
 */
function Caveat({
  runCount,
  unmeasuredRuns,
  unpricedRuns,
  link,
}: {
  runCount: number;
  unmeasuredRuns: number;
  unpricedRuns: number;
  link?: ReactNode;
}) {
  if (unmeasuredRuns === 0 && unpricedRuns === 0)
    return link ? (
      <p className="text-muted-foreground basis-full text-sm">{link}</p>
    ) : null;
  const of = (count: number) =>
    runCount >= count
      ? `${String(count)} of ${String(runCount)} ${runCount === 1 ? 'run' : 'runs'}`
      : count === 1
        ? '1 run'
        : `${String(count)} runs`;
  const parts: string[] = [];
  if (unmeasuredRuns > 0) parts.push(`${of(unmeasuredRuns)} not measured`);
  if (unpricedRuns > 0)
    parts.push(
      parts.length > 0
        ? `${String(unpricedRuns)} without a price`
        : `${of(unpricedRuns)} without a price`,
    );
  const runs = (count: number) =>
    count === 1 ? '1 run' : `${String(count)} runs`;

  return (
    <Collapsible className="basis-full" data-testid="spend-caveat">
      <div className="text-muted-foreground flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <span role="status">{parts.join(', ')}</span>
        <CollapsibleTrigger className={DISCLOSURE_TRIGGER}>
          <ChevronRightIcon aria-hidden="true" className={DISCLOSURE_CHEVRON} />
          Details
        </CollapsibleTrigger>
        {link}
      </div>
      <CollapsiblePanel>
        <p className="text-muted-foreground mt-2 text-sm">
          The figures on this page are incomplete, and so is the forecast.
          {unmeasuredRuns > 0 ? (
            <>
              {' '}
              {runs(unmeasuredRuns)} could not be measured, so nothing they
              consumed is counted anywhere on this page.
            </>
          ) : null}
          {unpricedRuns > 0 ? (
            <>
              {' '}
              {runs(unpricedRuns)} ran on a model with no price for that date,
              so their tokens are counted and their cost is not.
            </>
          ) : null}
        </p>
      </CollapsiblePanel>
    </Collapsible>
  );
}

/**
 * What sits here when no limit applies (FR-010).
 *
 * The page still shows every figure it has; only the ratio is absent, because there is
 * nothing to take a ratio against. Saying so plainly beats drawing an empty bar, which
 * would read as "nothing spent" rather than "nothing to compare".
 *
 * There is deliberately no call to action. A workspace owner cannot set a limit — that
 * moved to the organization administrator (FR-008, FR-034) — so offering one here would
 * be an affordance that leads nowhere.
 */
function NoLimit({
  spend,
  footer,
  caveat,
}: {
  spend: SpendTotals;
  footer?: ReactNode;
  caveat: ReactNode;
}) {
  const currency = spend.currency ?? 'USD';

  return (
    <section
      className={cn(USAGE_CARD, 'flex flex-col', USAGE_CARD_GAP)}
      aria-labelledby="usage-spend-heading"
    >
      <h2 id="usage-spend-heading" className="sr-only">
        Spending this period
      </h2>
      <div className="flex flex-col gap-1">
        <Total amount={spend.amount} currency={currency} />
        <p className="text-muted-foreground text-sm">
          No spend limit applies to this workspace.
        </p>
        {caveat}
      </div>
      {footer}
    </section>
  );
}
